import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  User, Building2, ArrowRight, ArrowLeft, Check, 
  AlertCircle, FileText, CreditCard, Shield
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const HostOnboarding = () => {
  const navigate = useNavigate();
  const { user, completeHostOnboarding, hasHostRole, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  // Redirect if not authenticated — wait for auth to finish loading first
  useEffect(() => {
    if (!authLoading && !isAuthenticated()) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const [currentStep, setCurrentStep] = useState(1);
  const [hostType, setHostType] = useState(null); // 'individual' or 'company'
  const [formData, setFormData] = useState({
    // Individual fields
    legalFullName: user?.name || '',
    dateOfBirth: '',
    residentialAddress: '',
    residentialCity: '',
    residentialPostcode: '',
    
    // Company fields
    companyName: '',
    companyRegistrationNumber: '',
    registeredOfficeAddress: '',
    registeredCity: '',
    registeredPostcode: '',
    vatNumber: '',
    directorName: '',
    
    // Bank details (both)
    bankAccountName: '',
    bankAccountNumber: '',
    bankSortCode: '',
    
    // Declarations
    taxDeclarationAccepted: false,
    termsAccepted: false
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const totalSteps = 4;

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!hostType) {
        newErrors.hostType = 'Please select a host type';
      }
    }
    
    if (step === 2) {
      if (hostType === 'individual') {
        if (!formData.legalFullName) newErrors.legalFullName = 'Full name is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!formData.residentialAddress) newErrors.residentialAddress = 'Address is required';
        if (!formData.residentialCity) newErrors.residentialCity = 'City is required';
        if (!formData.residentialPostcode) newErrors.residentialPostcode = 'Postcode is required';
      } else {
        if (!formData.companyName) newErrors.companyName = 'Company name is required';
        if (!formData.companyRegistrationNumber) newErrors.companyRegistrationNumber = 'Registration number is required';
        if (!formData.registeredOfficeAddress) newErrors.registeredOfficeAddress = 'Registered address is required';
        if (!formData.registeredCity) newErrors.registeredCity = 'City is required';
        if (!formData.registeredPostcode) newErrors.registeredPostcode = 'Postcode is required';
        if (!formData.directorName) newErrors.directorName = 'Director name is required';
      }
    }
    
    if (step === 3) {
      if (!formData.bankAccountName) newErrors.bankAccountName = 'Account name is required';
      if (!formData.bankAccountNumber) newErrors.bankAccountNumber = 'Account number is required';
      if (!formData.bankSortCode) newErrors.bankSortCode = 'Sort code is required';
      
      // Basic UK bank account validation
      if (formData.bankAccountNumber && !/^\d{8}$/.test(formData.bankAccountNumber)) {
        newErrors.bankAccountNumber = 'Account number must be 8 digits';
      }
      if (formData.bankSortCode && !/^\d{6}$/.test(formData.bankSortCode.replace(/-/g, ''))) {
        newErrors.bankSortCode = 'Sort code must be 6 digits';
      }
    }
    
    if (step === 4) {
      if (!formData.taxDeclarationAccepted) {
        newErrors.taxDeclarationAccepted = 'You must accept the tax declaration';
      }
      if (!formData.termsAccepted) {
        newErrors.termsAccepted = 'You must accept the terms and conditions';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setSaving(true);
    
    try {
      // Call backend to complete host onboarding
      const onboardingData = {
        hostType,
        ...formData
      };
      
      const result = await completeHostOnboarding(onboardingData);
      
      if (result.success) {
        toast({
          title: 'Onboarding Complete!',
          description: 'Your host profile is now pending verification. You can start listing properties.',
        });
        navigate('/host/dashboard');
      } else {
        throw new Error(result.message || 'Onboarding failed');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast({
        title: 'Onboarding Failed',
        description: error.response?.data?.detail || error.message || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step, index) => (
        <React.Fragment key={step}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
              step < currentStep 
                ? 'bg-green-500 text-white' 
                : step === currentStep 
                  ? 'bg-[#1F3A68] text-white' 
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {step < currentStep ? <Check size={20} /> : step}
          </div>
          {index < 3 && (
            <div className={`w-16 h-1 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Select Host Type</h2>
        <p className="text-gray-600 mt-2">Choose how you'll be hosting on Ariv</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg ${
            hostType === 'individual' ? 'ring-2 ring-[#1F3A68] bg-[#1F3A68]/5' : ''
          }`}
          onClick={() => setHostType('individual')}
          data-testid="host-type-individual"
        >
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-[#1F3A68]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <User size={32} className="text-[#1F3A68]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Individual Host</h3>
            <p className="text-gray-600 text-sm">
              Hosting as a sole trader or individual property owner
            </p>
            {hostType === 'individual' && (
              <div className="mt-4">
                <Check className="mx-auto text-[#1F3A68]" size={24} />
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg ${
            hostType === 'company' ? 'ring-2 ring-[#1F3A68] bg-[#1F3A68]/5' : ''
          }`}
          onClick={() => setHostType('company')}
          data-testid="host-type-company"
        >
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-[#1F3A68]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 size={32} className="text-[#1F3A68]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Registered Company</h3>
            <p className="text-gray-600 text-sm">
              Hosting through a limited company or business entity
            </p>
            {hostType === 'company' && (
              <div className="mt-4">
                <Check className="mx-auto text-[#1F3A68]" size={24} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {errors.hostType && (
        <p className="text-red-500 text-sm text-center flex items-center justify-center gap-1">
          <AlertCircle size={14} />
          {errors.hostType}
        </p>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">
          {hostType === 'individual' ? 'Personal Details' : 'Company Details'}
        </h2>
        <p className="text-gray-600 mt-2">Required for UK compliance</p>
      </div>
      
      {hostType === 'individual' ? (
        <div className="space-y-4 max-w-xl mx-auto">
          <div>
            <Label htmlFor="legalFullName">Legal Full Name *</Label>
            <Input
              id="legalFullName"
              placeholder="As shown on your ID"
              value={formData.legalFullName}
              onChange={(e) => updateFormData('legalFullName', e.target.value)}
              className={errors.legalFullName ? 'border-red-500' : ''}
              data-testid="input-legal-name"
            />
            {errors.legalFullName && <p className="text-red-500 text-sm mt-1">{errors.legalFullName}</p>}
          </div>
          
          <div>
            <Label htmlFor="dateOfBirth">Date of Birth *</Label>
            <Input
              id="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => updateFormData('dateOfBirth', e.target.value)}
              className={errors.dateOfBirth ? 'border-red-500' : ''}
              data-testid="input-dob"
            />
            {errors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>}
          </div>
          
          <div>
            <Label htmlFor="residentialAddress">Residential Address *</Label>
            <Input
              id="residentialAddress"
              placeholder="Street address"
              value={formData.residentialAddress}
              onChange={(e) => updateFormData('residentialAddress', e.target.value)}
              className={errors.residentialAddress ? 'border-red-500' : ''}
              data-testid="input-address"
            />
            {errors.residentialAddress && <p className="text-red-500 text-sm mt-1">{errors.residentialAddress}</p>}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="residentialCity">City *</Label>
              <Input
                id="residentialCity"
                placeholder="City"
                value={formData.residentialCity}
                onChange={(e) => updateFormData('residentialCity', e.target.value)}
                className={errors.residentialCity ? 'border-red-500' : ''}
                data-testid="input-city"
              />
              {errors.residentialCity && <p className="text-red-500 text-sm mt-1">{errors.residentialCity}</p>}
            </div>
            <div>
              <Label htmlFor="residentialPostcode">Postcode *</Label>
              <Input
                id="residentialPostcode"
                placeholder="e.g., SW1A 1AA"
                value={formData.residentialPostcode}
                onChange={(e) => updateFormData('residentialPostcode', e.target.value)}
                className={errors.residentialPostcode ? 'border-red-500' : ''}
                data-testid="input-postcode"
              />
              {errors.residentialPostcode && <p className="text-red-500 text-sm mt-1">{errors.residentialPostcode}</p>}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4 max-w-xl mx-auto">
          <div>
            <Label htmlFor="companyName">Company Name *</Label>
            <Input
              id="companyName"
              placeholder="Registered company name"
              value={formData.companyName}
              onChange={(e) => updateFormData('companyName', e.target.value)}
              className={errors.companyName ? 'border-red-500' : ''}
              data-testid="input-company-name"
            />
            {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
          </div>
          
          <div>
            <Label htmlFor="companyRegistrationNumber">Company Registration Number *</Label>
            <Input
              id="companyRegistrationNumber"
              placeholder="8 digit number"
              value={formData.companyRegistrationNumber}
              onChange={(e) => updateFormData('companyRegistrationNumber', e.target.value)}
              className={errors.companyRegistrationNumber ? 'border-red-500' : ''}
              data-testid="input-crn"
            />
            {errors.companyRegistrationNumber && <p className="text-red-500 text-sm mt-1">{errors.companyRegistrationNumber}</p>}
          </div>
          
          <div>
            <Label htmlFor="registeredOfficeAddress">Registered Office Address *</Label>
            <Input
              id="registeredOfficeAddress"
              placeholder="Street address"
              value={formData.registeredOfficeAddress}
              onChange={(e) => updateFormData('registeredOfficeAddress', e.target.value)}
              className={errors.registeredOfficeAddress ? 'border-red-500' : ''}
              data-testid="input-reg-address"
            />
            {errors.registeredOfficeAddress && <p className="text-red-500 text-sm mt-1">{errors.registeredOfficeAddress}</p>}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="registeredCity">City *</Label>
              <Input
                id="registeredCity"
                placeholder="City"
                value={formData.registeredCity}
                onChange={(e) => updateFormData('registeredCity', e.target.value)}
                className={errors.registeredCity ? 'border-red-500' : ''}
              />
              {errors.registeredCity && <p className="text-red-500 text-sm mt-1">{errors.registeredCity}</p>}
            </div>
            <div>
              <Label htmlFor="registeredPostcode">Postcode *</Label>
              <Input
                id="registeredPostcode"
                placeholder="e.g., SW1A 1AA"
                value={formData.registeredPostcode}
                onChange={(e) => updateFormData('registeredPostcode', e.target.value)}
                className={errors.registeredPostcode ? 'border-red-500' : ''}
              />
              {errors.registeredPostcode && <p className="text-red-500 text-sm mt-1">{errors.registeredPostcode}</p>}
            </div>
          </div>
          
          <div>
            <Label htmlFor="vatNumber">VAT Number (Optional)</Label>
            <Input
              id="vatNumber"
              placeholder="GB123456789"
              value={formData.vatNumber}
              onChange={(e) => updateFormData('vatNumber', e.target.value)}
              data-testid="input-vat"
            />
          </div>
          
          <div>
            <Label htmlFor="directorName">Director / Authorised Person Name *</Label>
            <Input
              id="directorName"
              placeholder="Full name"
              value={formData.directorName}
              onChange={(e) => updateFormData('directorName', e.target.value)}
              className={errors.directorName ? 'border-red-500' : ''}
              data-testid="input-director"
            />
            {errors.directorName && <p className="text-red-500 text-sm mt-1">{errors.directorName}</p>}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <CreditCard size={48} className="mx-auto text-[#1F3A68] mb-4" />
        <h2 className="text-2xl font-bold text-gray-900">Bank Payout Details</h2>
        <p className="text-gray-600 mt-2">Where you'll receive your booking payments</p>
      </div>
      
      <div className="space-y-4 max-w-xl mx-auto">
        <div>
          <Label htmlFor="bankAccountName">Account Holder Name *</Label>
          <Input
            id="bankAccountName"
            placeholder="Name on the bank account"
            value={formData.bankAccountName}
            onChange={(e) => updateFormData('bankAccountName', e.target.value)}
            className={errors.bankAccountName ? 'border-red-500' : ''}
            data-testid="input-bank-name"
          />
          {errors.bankAccountName && <p className="text-red-500 text-sm mt-1">{errors.bankAccountName}</p>}
        </div>
        
        <div>
          <Label htmlFor="bankAccountNumber">Account Number *</Label>
          <Input
            id="bankAccountNumber"
            placeholder="8 digit account number"
            value={formData.bankAccountNumber}
            onChange={(e) => updateFormData('bankAccountNumber', e.target.value.replace(/\D/g, '').slice(0, 8))}
            className={errors.bankAccountNumber ? 'border-red-500' : ''}
            data-testid="input-bank-number"
          />
          {errors.bankAccountNumber && <p className="text-red-500 text-sm mt-1">{errors.bankAccountNumber}</p>}
        </div>
        
        <div>
          <Label htmlFor="bankSortCode">Sort Code *</Label>
          <Input
            id="bankSortCode"
            placeholder="e.g., 12-34-56"
            value={formData.bankSortCode}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, '').slice(0, 6);
              if (value.length > 2) value = value.slice(0, 2) + '-' + value.slice(2);
              if (value.length > 5) value = value.slice(0, 5) + '-' + value.slice(5);
              updateFormData('bankSortCode', value);
            }}
            className={errors.bankSortCode ? 'border-red-500' : ''}
            data-testid="input-bank-sort"
          />
          {errors.bankSortCode && <p className="text-red-500 text-sm mt-1">{errors.bankSortCode}</p>}
        </div>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Shield className="text-blue-600 shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-blue-800">
              <p className="font-semibold">Your details are secure</p>
              <p>We use bank-grade encryption to protect your financial information.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <FileText size={48} className="mx-auto text-[#1F3A68] mb-4" />
        <h2 className="text-2xl font-bold text-gray-900">Declarations</h2>
        <p className="text-gray-600 mt-2">Please review and accept the following</p>
      </div>
      
      <div className="space-y-6 max-w-xl mx-auto">
        <Card className={`${errors.taxDeclarationAccepted ? 'border-red-500' : ''}`}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Checkbox
                id="taxDeclaration"
                checked={formData.taxDeclarationAccepted}
                onCheckedChange={(checked) => updateFormData('taxDeclarationAccepted', checked)}
                data-testid="checkbox-tax"
              />
              <div className="flex-1">
                <Label htmlFor="taxDeclaration" className="text-base font-semibold cursor-pointer">
                  Tax Declaration *
                </Label>
                <p className="text-gray-600 text-sm mt-2">
                  I acknowledge that I am responsible for reporting and paying any applicable taxes 
                  on income earned through Ariv to HMRC. I understand that Ariv
                  does not provide tax advice and recommend consulting with a qualified tax professional.
                </p>
              </div>
            </div>
            {errors.taxDeclarationAccepted && (
              <p className="text-red-500 text-sm mt-2 ml-8">{errors.taxDeclarationAccepted}</p>
            )}
          </CardContent>
        </Card>
        
        <Card className={`${errors.termsAccepted ? 'border-red-500' : ''}`}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Checkbox
                id="terms"
                checked={formData.termsAccepted}
                onCheckedChange={(checked) => updateFormData('termsAccepted', checked)}
                data-testid="checkbox-terms"
              />
              <div className="flex-1">
                <Label htmlFor="terms" className="text-base font-semibold cursor-pointer">
                  Terms & Conditions *
                </Label>
                <p className="text-gray-600 text-sm mt-2">
                  I agree to the Ariv{' '}
                  <a href="/terms#host-terms" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Host Terms of Service</a>
                  {' '}(Section 5) and{' '}
                  <a href="/terms#data-protection" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Privacy Policy</a>,
                  including terms for auto-dispatch, Smart Lock, and Right-to-Rent compliance.
                  I confirm that all information provided is accurate and complete.
                </p>
              </div>
            </div>
            {errors.termsAccepted && (
              <p className="text-red-500 text-sm mt-2 ml-8">{errors.termsAccepted}</p>
            )}
          </CardContent>
        </Card>
        
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Compliance Status</p>
                <p>
                  After submission, your profile will be in "Pending Verification" status. 
                  You can start listing properties, but payouts will be held until verification is complete.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
            </Link>
            <div className="text-sm text-gray-500">
              Step {currentStep} of {totalSteps}
            </div>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Add-role banner — consistent with the other partner-role
              signup pages. HostOnboarding is auth-required, so every
              visitor here is expanding an existing account. */}
          {user && (
            <div className="mb-4 p-3 bg-[#1F3A68]/5 border border-[#1F3A68]/20 rounded-lg text-sm text-[#1F3A68]">
              <p className="font-semibold">Adding Host role to your existing account</p>
              <p className="text-xs text-[#1F3A68]/80 mt-0.5 break-all">
                Linked to {user.email}. One login, every role.
              </p>
            </div>
          )}

          {renderStepIndicator()}

          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
              
              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft size={16} />
                  Back
                </Button>
                
                {currentStep < totalSteps ? (
                  <Button
                    onClick={handleNext}
                    className="bg-[#1F3A68] hover:bg-[#1F3A68]/90 flex items-center gap-2"
                    data-testid="onboarding-next-btn"
                  >
                    Next
                    <ArrowRight size={16} />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90 flex items-center gap-2"
                    disabled={saving}
                    data-testid="onboarding-submit-btn"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Complete Setup
                        <Check size={16} />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HostOnboarding;
